import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import { Box } from '@mui/material';
import { Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import AppButton from '../../../../components/ui/AppButton';
import { useGetRewardList, useDeleteReward } from '../../../../hooks/useReward';
import { useDialogStore } from '../../../../stores/dialogStore';
import DeleteRewardDialog from './DeleteRewardDialog';

const RewardManagement = () => {
  const navigate = useNavigate();

  const openDialog = useDialogStore((state) => state.openDialog);

  const [selectedReward, setSelectedReward] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);

  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
    sort_col: 'point',
    sort_value: true,
  });

  const { data: rewardData, isLoading: isLoadingReward, refetch } = useGetRewardList(query);
  const { mutateAsync: deleteReward, isPending: isLoadingDeleteReward } = useDeleteReward();

  const columns = [
    {
      title: 'รูปภาพ',
      key: 'image',
      width: 130,
      align: 'center',
      render: (value, row) => {
        if (row.image && row.image[0]) {
          return (
            <Box sx={{ backgroundColor: 'white', borderRadius: '4px' }}>
              <Box
                component="img"
                src={row.image[0]}
                alt={row.name}
                sx={{ maxWidth: 120, display: 'block', margin: '0 auto' }}
              />
            </Box>
          );
        }

        return <ImageIcon sx={{ height: '100px', display: 'block', margin: '0 auto' }} />;
      },
    },
    {
      title: 'ชื่อของรางวัล',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: true,
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: 'คะแนน',
      dataIndex: 'point',
      key: 'point',
      width: 120,
      sorter: true,
      align: 'center',
      render: (value) => value || '0',
      defaultSortOrder: 'ascend',
    },
    {
      title: 'จำนวนที่ได้รับ',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 160,
      sorter: true,
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: 'คงเหลือ',
      dataIndex: 'stock',
      key: 'stock',
      width: 120,
      sorter: true,
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: 'ประเภท',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      sorter: true,
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: 'ขนาดสินค้า',
      dataIndex: 'product_size',
      key: 'product_size',
      width: 150,
      sorter: true,
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: 'การจัดส่ง',
      dataIndex: 'type_delivery',
      key: 'type_delivery',
      width: 150,
      sorter: true,
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: 'Action',
      key: 'action',
      width: 250,
      align: 'center',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <AppButton variant="primary" onClick={() => navigate(`/admin/reward/${row._id}`)}>
            <ModeEditIcon sx={{ fontSize: '1.25rem' }} />
            Edit
          </AppButton>
          <AppButton
            variant="dangerStrong"
            onClick={() => {
              setSelectedReward(row);
              setDeleteDialog(true);
            }}
          >
            <DeleteIcon sx={{ fontSize: '1.25rem' }} />
            Delete
          </AppButton>
        </Box>
      ),
    },
  ];

  const handleDeleteReward = async (reward) => {
    try {
      const response = await deleteReward({ params: { id: reward._id } });

      if (response.Code === '20100') {
        openDialog({
          title: 'ลบรางวัลสำเร็จ',
          message: 'ลบรางวัลสำเร็จ',
          variant: 'success',
          onCloseCallback: () => refetch(),
        });
      }
    } catch (error) {
      console.error(error);
      openDialog({
        title: 'ลบรางวัลไม่สำเร็จ',
        message: 'ลบรางวัลไม่สำเร็จ',
        variant: 'error',
      });
    } finally {
      setDeleteDialog(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          padding: '16px',
          backgroundColor: 'white',
          borderBottomLeftRadius: '15px',
          borderBottomRightRadius: '15px',
          border: '2px solid white',
          boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
          '& .ant-pagination': {
            alignItems: 'center',
          },
          '& .ant-pagination-options': {
            alignSelf: 'center',
          },
        }}
      >
        <Table
          bordered
          rowKey="_id"
          columns={columns}
          dataSource={rewardData?.Data?.data || []}
          loading={isLoadingReward}
          pagination={{
            current: query.page,
            pageSize: query.limit,
            total: rewardData?.Data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '50', '100', '200'],
            hideOnSinglePage: true,
          }}
          onChange={(pagination, filters, sorter) => {
            setQuery((prev) => ({
              ...prev,
              page: pagination.current,
              limit: pagination.pageSize,
              sort_col: sorter.field || 'point',
              sort_value: sorter.order === 'ascend',
            }));
          }}
          scroll={{
            x: 'max-content',
          }}
          rowHoverable={true}
        />
      </Box>

      {/* Delete Reward Dialog */}
      <DeleteRewardDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDeleteReward}
        data={selectedReward}
        isLoading={isLoadingDeleteReward}
      />
    </Box>
  );
};
export default RewardManagement;
